import {
  Download,
  Lock,
  RotateCcw,
  SlidersHorizontal,
  Unlock,
  X,
} from 'lucide-react'

import {
  useMemo,
  useState,
} from 'react'

import {
  extensionRegistry,
} from '../extensions/registry'

import {
  useLayout,
} from '../preferences/LayoutContext'

import {
  useContextualSettings,
} from '../contextual/ContextualSettingsContext'

type Preset =
  | 'GENESIS'
  | 'COMMAND'
  | 'STUDIO'
  | 'LIGHT_PRO'

const PRESET_VALUES = {
  GENESIS: {
    background:
      '#06111a',

    text:
      '#e4edf5',

    theme:
      'GENESIS',
  },

  COMMAND: {
    background:
      '#050a0f',

    text:
      '#e9f0f4',

    theme:
      'GRAPHITE',
  },

  STUDIO: {
    background:
      '#07121c',

    text:
      '#edf4f8',

    theme:
      'MIDNIGHT',
  },

  LIGHT_PRO: {
    background:
      '#eef3f6',

    text:
      '#172832',

    theme:
      'LIGHT',
  },
} as const

export function CustomizationPanel() {
  const [
    open,
    setOpen,
  ] =
    useState(false)

  const {
    preferences,
    update,
    restore,
  } =
    useLayout()

  const {
    settings,
    get,
    patch,
    resetAll,
  } =
    useContextualSettings()

  const modules =
    useMemo(
      () =>
        extensionRegistry
          .modules(),
      [],
    )

  function applyPreset(
    preset:
      Preset,
  ) {
    const values =
      PRESET_VALUES[preset]

    update({
      layoutPreset:
        preset,

      theme:
        values.theme,

      backgroundBaseColor:
        values.background,

      textColor:
        values.text,

      clockTimeColor:
        preset ===
        'LIGHT_PRO'
          ? '#087f8c'
          : '#64dbe0',

      clockDateColor:
        preset ===
        'LIGHT_PRO'
          ? '#647985'
          : '#7893a2',
    })
  }

  function exportDesign() {
    const payload = {
      schema:
        'SEI-GENESIS-DESIGN-R7',

      exportedAt:
        new Date()
          .toISOString(),

      preferences,

      contextual:
        settings,
    }

    const blob =
      new Blob(
        [
          JSON.stringify(
            payload,
            null,
            2,
          ),
        ],
        {
          type:
            'application/json',
        },
      )

    const url =
      URL.createObjectURL(
        blob,
      )

    const anchor =
      document.createElement(
        'a',
      )

    anchor.href =
      url

    anchor.download =
      'sei-genesis-design-r7.json'

    anchor.click()

    URL.revokeObjectURL(
      url,
    )
  }

  return (
    <>
      <button
        type="button"
        className="secondary-button"
        onClick={() =>
          setOpen(true)
        }
      >
        <SlidersHorizontal
          size={16}
        />

        Personalizar
      </button>

      {open && (
        <div className="custom-overlay">

          <aside className="custom-panel r7-global-panel">

            <header>

              <div>
                <strong>
                  Diseño Genesis
                </strong>

                <span>
                  Configuración global
                </span>
              </div>

              <button
                type="button"
                className="r7-icon-button"
                onClick={() =>
                  setOpen(false)
                }
              >
                <X size={17} />
              </button>

            </header>

            <section>

              <h3>
                Máscara
              </h3>

              <select
                value={
                  preferences
                    .layoutPreset
                }
                onChange={
                  event =>
                    applyPreset(
                      event.target
                        .value as
                        Preset,
                    )
                }
              >
                <option value="GENESIS">
                  Genesis
                </option>

                <option value="COMMAND">
                  Command
                </option>

                <option value="STUDIO">
                  Studio
                </option>

                <option value="LIGHT_PRO">
                  Light Professional
                </option>
              </select>

            </section>

            <section>

              <h3>
                Fondo general
              </h3>

              <label className="r7-inline-control">

                <span>
                  Color
                </span>

                <input
                  type="color"
                  value={
                    preferences
                      .backgroundBaseColor
                  }
                  onChange={
                    event =>
                      update({
                        backgroundBaseColor:
                          event.target.value,
                      })
                  }
                />

              </label>

              <label className="r7-range">

                <span>
                  Intensidad

                  <strong>
                    {
                      preferences
                        .backgroundIntensity
                    }%
                  </strong>
                </span>

                <input
                  type="range"
                  min="30"
                  max="100"
                  step="1"
                  value={
                    preferences
                      .backgroundIntensity
                  }
                  onChange={
                    event =>
                      update({
                        backgroundIntensity:
                          Number(
                            event.target
                              .value,
                          ),
                      })
                  }
                />

              </label>

              <label className="r7-range">

                <span>
                  Contraste ambiental

                  <strong>
                    {
                      preferences
                        .backgroundContrast
                    }%
                  </strong>
                </span>

                <input
                  type="range"
                  min="0"
                  max="100"
                  step="1"
                  value={
                    preferences
                      .backgroundContrast
                  }
                  onChange={
                    event =>
                      update({
                        backgroundContrast:
                          Number(
                            event.target
                              .value,
                          ),
                      })
                  }
                />

              </label>

            </section>

            <section>

              <h3>
                Diseño
              </h3>

              <label className="r7-toggle">

                <input
                  type="checkbox"
                  checked={
                    preferences
                      .designMode
                  }
                  onChange={
                    event =>
                      update({
                        designMode:
                          event.target
                            .checked,
                      })
                  }
                />

                Modo diseño

              </label>

              <button
                type="button"
                className="r7-wide-action"
                onClick={() =>
                  update({
                    designLocked:
                      !preferences
                        .designLocked,
                  })
                }
              >

                {
                  preferences
                    .designLocked
                    ? <Lock size={15} />
                    : <Unlock size={15} />
                }

                {
                  preferences
                    .designLocked
                    ? 'Diseño bloqueado'
                    : 'Diseño desbloqueado'
                }

              </button>

              <p>
                Desbloquea sólo cuando
                quieras mover módulos.
              </p>

            </section>

            <details className="r7-catalog">

              <summary>
                Catálogo de módulos
              </summary>

              <div>

                {modules.map(
                  module => {
                    const id =
                      `module:${module.id}`

                    return (
                      <label
                        key={
                          module.id
                        }
                        className="r7-catalog-row"
                      >

                        <span>

                          <strong>
                            {module.name}
                          </strong>

                          <small>
                            {
                              module
                                .description
                            }
                          </small>

                        </span>

                        <input
                          type="checkbox"
                          checked={
                            get(id)
                              .visible
                          }
                          onChange={
                            event =>
                              patch(
                                id,
                                {
                                  visible:
                                    event.target
                                      .checked,
                                },
                              )
                          }
                        />

                      </label>
                    )
                  },
                )}

              </div>

            </details>

            <section className="r7-global-help">

              <strong>
                Personalización contextual
              </strong>

              <p>
                Usa ⋮ directamente en
                Ingeniería, Sistema/VPS,
                Autorización, LAIA,
                botones superiores y reloj.
              </p>

            </section>

            <button
              type="button"
              className="r7-wide-action"
              onClick={
                exportDesign
              }
            >
              <Download size={15} />

              Exportar diseño
            </button>

            <button
              type="button"
              className="restore-layout"
              onClick={() => {
                if (
                  !window.confirm(
                    '¿Restaurar completamente el diseño de Genesis?',
                  )
                ) {
                  return
                }

                restore()
                resetAll()
                setOpen(false)
              }}
            >
              <RotateCcw
                size={16}
              />

              Restaurar diseño completo

            </button>

          </aside>

        </div>
      )}
    </>
  )
}
